/**
 * Shared utilities for PDF Sign application
 */

// ============================================
// Crypto Utilities
// ============================================

/**
 * Generate a cryptographically secure random string
 */
export function generateSecureToken(length = 32): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a UUID v4
 */
export function generateUUID(): string {
  return crypto.randomUUID();
}

/**
 * Hash a string using SHA-256
 */
export async function sha256(input: string | ArrayBuffer): Promise<string> {
  const data = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash content (string or bytes) with SHA-256, returned as hex.
 * Alias kept for API route ergonomics; identical to `sha256`.
 */
export async function hashContent(input: string | ArrayBuffer | Uint8Array): Promise<string> {
  const data =
    typeof input === 'string'
      ? input
      : input instanceof Uint8Array
        ? (input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength) as ArrayBuffer)
        : input;
  return sha256(data as string | ArrayBuffer);
}

/**
 * Constant-time string comparison.
 *
 * Implemented manually (instead of crypto.subtle.timingSafeEqual) so it works
 * identically in Workers, browsers and Node without type/feature differences.
 */
export async function timingSafeEqual(a: string, b: string): Promise<boolean> {
  const encoder = new TextEncoder();
  const aBytes = encoder.encode(a);
  const bBytes = encoder.encode(b);
  const length = Math.max(aBytes.length, bBytes.length);

  let diff = aBytes.length ^ bBytes.length;
  for (let i = 0; i < length; i++) {
    diff |= (aBytes[i] ?? 0) ^ (bBytes[i] ?? 0);
  }
  return diff === 0;
}

// ============================================
// Date/Time Utilities
// ============================================

export function nowISO(): string {
  return new Date().toISOString();
}

export function addDays(date: Date | string, days: number): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  d.setDate(d.getDate() + days);
  return d;
}

export function addHours(date: Date | string, hours: number): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  d.setHours(d.getHours() + hours);
  return d;
}

export function addMinutes(date: Date | string, minutes: number): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  d.setMinutes(d.getMinutes() + minutes);
  return d;
}

export function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

export function formatDateTime(date: Date | string, locale = 'vi-VN', timeZone = 'Asia/Ho_Chi_Minh'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString(locale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatDate(date: Date | string, locale = 'vi-VN', timeZone = 'Asia/Ho_Chi_Minh'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatRelativeTime(date: Date | string, locale = 'vi-VN'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Vừa xong';
  if (diffMins < 60) return `${diffMins} phút trước`;
  if (diffHours < 24) return `${diffHours} giờ trước`;
  if (diffDays < 7) return `${diffDays} ngày trước`;
  return formatDate(d, locale);
}

// ============================================
// String Utilities
// ============================================

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[đĐ]/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function truncate(text: string, maxLength: number, suffix = '...'): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length) + suffix;
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
}

export function maskPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 4) return '****';
  return `${cleaned.slice(0, 2)}****${cleaned.slice(-2)}`;
}

// ============================================
// File Utilities
// ============================================

export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function isValidPDF(file: File | { type: string; name: string }): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function getMimeTypeFromExtension(ext: string): string {
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    txt: 'text/plain',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
  return mimeTypes[ext.toLowerCase()] || 'application/octet-stream';
}

// ============================================
// Validation
// ============================================

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidVietnamesePhone(phone: string): boolean {
  // Vietnamese phone formats: 0xxxxxxxxx, +84xxxxxxxxx, 84xxxxxxxxx
  const phoneRegex = /^(?:\+84|84|0)(?:[35789]\d{8})$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
}

export function isStrongPassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (password.length < 8) errors.push('Ít nhất 8 ký tự');
  if (!/[A-Z]/.test(password)) errors.push('Chứa ít nhất 1 chữ hoa');
  if (!/[a-z]/.test(password)) errors.push('Chứa ít nhất 1 chữ thường');
  if (!/[0-9]/.test(password)) errors.push('Chứa ít nhất 1 số');
  if (!/[!@#$%^&*]/.test(password)) errors.push('Chứa ít nhất 1 ký tự đặc biệt (!@#$%^&*)');
  return { valid: errors.length === 0, errors };
}

// ============================================
// R2 Key Generation
// ============================================

export function generateR2Key(prefix: string, filename: string, userId?: string): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const ext = getFileExtension(filename);
  const unique = generateSecureToken(8);
  const userPart = userId ? `${userId}/` : '';
  return `${prefix}/${userPart}${year}/${month}/${day}/${unique}.${ext}`;
}

export function generateDocumentR2Key(organizationId: string, documentId: string, filename: string): string {
  return `documents/${organizationId}/${documentId}/${generateSecureToken(8)}_${filename}`;
}

export function generateSignatureR2Key(organizationId: string, documentId: string, signerId: string, filename: string): string {
  return `signatures/${organizationId}/${documentId}/${signerId}/${generateSecureToken(8)}_${filename}`;
}

// ============================================
// Error Handling
// ============================================

export class AppError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode = 400,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AppError';
  }

  static unauthorized(message = 'Unauthorized', details?: Record<string, unknown>) {
    return new AppError('UNAUTHORIZED', message, 401, details);
  }

  static forbidden(message = 'Forbidden', details?: Record<string, unknown>) {
    return new AppError('FORBIDDEN', message, 403, details);
  }

  static notFound(message = 'Not found', details?: Record<string, unknown>) {
    return new AppError('NOT_FOUND', message, 404, details);
  }

  static conflict(message = 'Conflict', details?: Record<string, unknown>) {
    return new AppError('CONFLICT', message, 409, details);
  }

  static validation(message = 'Validation failed', details?: Record<string, unknown>) {
    return new AppError('VALIDATION_ERROR', message, 400, details);
  }

  static internal(message = 'Internal server error', details?: Record<string, unknown>) {
    return new AppError('INTERNAL_ERROR', message, 500, details);
  }

  static badRequest(message = 'Bad request', details?: Record<string, unknown>) {
    return new AppError('BAD_REQUEST', message, 400, details);
  }

  static tooManyRequests(message = 'Too many requests', details?: Record<string, unknown>) {
    return new AppError('TOO_MANY_REQUESTS', message, 429, details);
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

// ============================================
// Pagination
// ============================================

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginationResult {
  page: number;
  limit: number;
  offset: number;
}

export function parsePagination(params: PaginationParams, maxLimit = 100): PaginationResult {
  const page = Math.max(1, params.page ?? 1);
  const limit = Math.min(maxLimit, Math.max(1, params.limit ?? 20));
  const offset = (page - 1) * limit;
  return { page, limit, offset };
}

export function createPaginationMeta(page: number, limit: number, total: number): {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
} {
  return {
    page,
    limit,
    total,
    total_pages: Math.ceil(total / limit),
  };
}

// ============================================
// Environment Helpers
// ============================================

export function getEnv<T>(env: Record<string, string>, key: string, defaultValue?: T): T | undefined {
  const value = env[key];
  if (value === undefined) return defaultValue;
  // Try to parse JSON
  try {
    return JSON.parse(value) as T;
  } catch {
    return value as unknown as T;
  }
}

export function requireEnv(env: Record<string, string>, key: string): string {
  const value = env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}